import { type Component, createEffect, createMemo, createResource, createSignal, onMount } from 'solid-js'
import { twMerge } from 'tailwind-merge'
import Select from '~/components/Select'
import { type GeoItem, geoService } from '~/services/geo.service'
import type { GeoSelectsPropsType } from '~/types/GeoSelects'

/**
 * Tres Selects en cascada (país → departamento → ciudad) alimentados por el
 * servicio geo del backend. El backend consulta la DB primero y hace fallback
 * a la librería country-state-city. El valor que se guarda es el label
 * (ej: 'Colombia', 'Cundinamarca', 'Bogotá').
 */

const toOptions = (items: GeoItem[]) => items.map(item => ({ value: item.label, title: item.label }))

// ── Countries compartido entre todas las instancias ────────────────────────
// Se crea una sola vez a nivel de módulo. La primera instancia dispara la
// petición; las demás simplemente leen el mismo recurso.
const [sharedCountries] = createResource(async () => {
  try {
    return await geoService.countries()
  } catch {
    return [] as GeoItem[]
  }
})

const GeoSelects: Component<GeoSelectsPropsType> = props => {
  const [departamentos, setDepartamentos] = createSignal<GeoItem[]>([])
  const [ciudades, setCiudades] = createSignal<GeoItem[]>([])
  const [pais, setPais] = createSignal(props.valuePais ?? '')
  const [departamento, setDepartamento] = createSignal(props.valueDepartamento ?? '')
  const [ciudad, setCiudad] = createSignal(props.valueCiudad ?? '')

  // Reaccionar a cambios externos de props (ej: geocodificación inversa)
  createEffect(() => {
    const val = props.valueDepartamento
    if (val !== undefined && val !== departamento()) setDepartamento(val)
  })
  createEffect(() => {
    const val = props.valueCiudad
    if (val !== undefined && val !== ciudad()) setCiudad(val)
  })

  // Datos legados: si hay ciudad guardada pero no departamento (se digitaban a
  // mano antes de los select), se resuelve el departamento de esa ciudad.
  onMount(() => {
    const ciudadValue = props.valueCiudad
    if (!ciudadValue || props.valueDepartamento) return
    const countryKey = paisKey()
    if (!countryKey) return
    geoService
      .searchCities(ciudadValue, countryKey)
      .then(async results => {
        if (results.length !== 1) return
        const city = results[0]
        const stateCode = (city.meta as Record<string, unknown>)?.stateCode as string | undefined
        if (!stateCode) return
        // Buscar el departamento por código ISO
        const states = await geoService.states(countryKey)
        const dept = states.find(s => s.key === stateCode)
        if (dept) setDepartamento(dept.label)
      })
      .catch(() => {})
  })

  const paisKey = createMemo(() => {
    const label = pais()
    if (!label) return undefined
    return sharedCountries()?.find(p => p.label === label)?.key
  })

  const departamentoKey = createMemo(() => {
    const label = departamento()
    if (!label) return undefined
    return departamentos().find(d => d.label === label)?.key
  })

  createEffect(() => {
    const key = paisKey()
    if (!key) {
      setDepartamentos([])
      setCiudades([])
      return
    }
    geoService
      .states(key)
      .then(setDepartamentos)
      .catch(() => setDepartamentos([]))
  })

  createEffect(() => {
    const countryKey = paisKey()
    const stateKey = departamentoKey()
    if (!countryKey || !stateKey) {
      setCiudades([])
      return
    }
    geoService
      .cities(countryKey, stateKey)
      .then(setCiudades)
      .catch(() => setCiudades([]))
  })

  const handlePaisChange = (value: unknown) => {
    setPais(String(value ?? ''))
    setDepartamento('')
    setCiudad('')
  }

  const handleDepartamentoChange = (value: unknown) => {
    setDepartamento(String(value ?? ''))
    setCiudad('')
  }

  return (
    <div class={twMerge('grid grid-cols-1 gap-4', props.class)}>
      <Select name={props.namePais} label="País" options={toOptions(sharedCountries() ?? [])} value={pais()} onChange={handlePaisChange} disabled={props.disabled} size={props.size} clearable />
      <Select
        name={props.nameDepartamento}
        label="Departamento"
        options={toOptions(departamentos())}
        value={departamento()}
        onChange={handleDepartamentoChange}
        disabled={props.disabled || departamentos().length === 0}
        size={props.size}
        clearable
      />
      <Select
        name={props.nameCiudad}
        label="Ciudad"
        options={toOptions(ciudades())}
        value={ciudad()}
        onChange={value => setCiudad(String(value ?? ''))}
        disabled={props.disabled || ciudades().length === 0}
        size={props.size}
        clearable
        autocomplete
      />
    </div>
  )
}

export default GeoSelects
